import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, SearchIcon, Select } from '../../../components/ui';
import { formatDate as formatDateGlobal, formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, defaultPeriodDates } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';
import { accountingReturnState } from '../shared/navigation';

type Row = {
  entityType: string;
  entityId: number;
  code: string | null;
  name: string | null;
  action: 'created' | 'updated';
  userName: string | null;
  timestamp: string;
};

type Response = { rows: Row[] };

const entityLink = (row: Row) => {
  if (row.entityType === 'document') return `/accounting/journals/${row.entityId}/edit`;
  if (row.entityType === 'account') return `/accounting/coa/${row.entityId}/edit`;
  if (row.entityType === 'period') return `/accounting/periods`;
  return null;
};

export const AuditTrail: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const defaults = useMemo(() => defaultPeriodDates(), []);
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [entityType, setEntityType] = useState('');
  const [run, setRun] = useState(false);

  const reportUrl =
    run
      ? buildReportQuery('/accounting/reports/audit-trail', {
          dateFrom,
          dateTo,
          entityType: entityType || undefined,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);

  const returnState = useMemo(
    () => accountingReturnState(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'timestamp', header: 'When', render: (v) => formatDateTime(v) },
      { key: 'entityType', header: 'Type' },
      {
        key: 'code',
        header: 'Code',
        render: (v, row) => {
          const href = entityLink(row);
          return href && v ? (
            <Link to={href} state={returnState} className="text-indigo-600 hover:underline">
              {v}
            </Link>
          ) : (
            v ?? `#${row.entityId}`
          );
        },
      },
      { key: 'name', header: 'Name', render: (v) => v ?? '—' },
      { key: 'action', header: 'Action' },
      { key: 'userName', header: 'User', render: (v) => v ?? '—' },
    ],
    [returnState],
  );

  const entityOptions = [
    { value: '', label: 'All entities' },
    { value: 'document', label: 'Documents' },
    { value: 'account', label: 'Accounts' },
    { value: 'period', label: 'Periods' },
  ];

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div>
          <FieldLabel>From</FieldLabel>
          <DateInput value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
        <div>
          <FieldLabel>To</FieldLabel>
          <DateInput value={dateTo} onChange={(e) => { setDateTo(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
        <div className="min-w-[10rem]">
          <FieldLabel>Entity</FieldLabel>
          <Select value={entityType} onChange={(e) => { setEntityType(e.target.value); setRun(false); }} options={entityOptions} />
        </div>
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Button variant="default" onClick={() => setRun(true)} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  const rows = data?.rows ?? [];

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Audit Trail"
      exportFileBase="audit-trail"
      exportDateTo={dateTo}
      statusBadge={
        run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(dateFrom)} – {formatDateGlobal(dateTo)}
          </Badge>
        ) : undefined
      }
      recordCount={rows.length}
      readyMessage="Set date range and entity filter, then search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => `${r.entityType}-${r.entityId}-${r.action}-${r.timestamp}`}
        stickyHeader
        emptyMessage="No activity in range."
      />
    </AccountingReportShell>
  );
};
